import React, { useCallback, useEffect, useState, useRef } from 'react';
import { Box, Typography, keyframes } from '@mui/material';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

// CSS animasyonları
const blink = keyframes`
  0% { opacity: 1; }
  50% { opacity: 0; }
  100% { opacity: 1; }
`;

const pulse = keyframes`
  0% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.7; transform: scale(1.1); }
  100% { opacity: 1; transform: scale(1); }
`;

const Footer = ({ symbols }) => {
  // localStorage'dan footer verilerini yükle
  const getInitialFooterData = () => {
    try {
      const savedData = localStorage.getItem('footerData');
      if (savedData) {
        const parsed = JSON.parse(savedData);
        // Sadece geçerli verileri kullan (son 30 dakika içinde güncellenmiş)
        const now = Date.now();
        if (parsed.timestamp && (now - parsed.timestamp) < 1800000) { // 30 dakika
          console.log('Footer: localStorage\'dan veriler yüklendi');
          return parsed.data || {
            ons: { value: 0, direction: 'up' },
            usd: { value: 0, direction: 'up' },
            euro: { value: 0, direction: 'up' },
            parite: { value: 0, direction: 'up' },
            has: { value: 0, direction: 'up' },
          };
        }
      }
    } catch (error) {
      console.error('Footer localStorage verileri yüklenirken hata:', error);
    }
    return {
      ons: { value: 0, direction: 'up' },
      usd: { value: 0, direction: 'up' },
      euro: { value: 0, direction: 'up' },
      parite: { value: 0, direction: 'up' },
      has: { value: 0, direction: 'up' },
    };
  };

  const [footerData, setFooterData] = useState(getInitialFooterData);
  
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [useHttpFallback, setUseHttpFallback] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const retryCountRef = useRef(0);
  const isConnectingRef = useRef(false);
  const lastMessageTimeRef = useRef(Date.now());
  const heartbeatIntervalRef = useRef(null);
  const symbolsRef = useRef(symbols);

  // symbols değiştiğinde ref'i güncelle
  useEffect(() => {
    symbolsRef.current = symbols;
  }, [symbols]);

  const createNewData = useCallback((newValue, oldValue) => {
    let direction = oldValue > 0 ? 'up' : null;

    if (newValue > oldValue) {
      direction = 'up';
    } else if (newValue < oldValue) {
      direction = 'down';
    }

    return { value: newValue, direction };
  }, []);

  // Footer verilerini localStorage'a kaydet
  const saveFooterDataToStorage = useCallback((newData) => {
    try {
      const dataToSave = {
        data: newData,
        timestamp: Date.now()
      };
      localStorage.setItem('footerData', JSON.stringify(dataToSave));
    } catch (error) {
      console.error('Footer verileri localStorage\'a kaydedilirken hata:', error);
    }
  }, []);

  // HTTP fallback ile fiyat verilerini çek
  const fetchFooterDataViaHttp = useCallback(async () => {
    try {
      const response = await fetch('/api/price');
      if (response.ok) {
        const data = await response.json();
        const currentSymbols = symbolsRef.current;
        
        if (currentSymbols && currentSymbols.length > 0) {
          const filteredData = data.filter((item) => currentSymbols.includes(item.name));
          const newFooterData = {};
          
          filteredData.forEach((item) => {
            const askValue = parseFloat(item.Ask) || 0;
            if (askValue > 0) {
              if (item.name === 'ONS') {
                newFooterData.ons = createNewData(askValue, footerData.ons.value);
              }
              if (item.name === 'USDTRY') {
                newFooterData.usd = createNewData(askValue, footerData.usd.value);
              }
              if (item.name === 'EURTRY') {
                newFooterData.euro = createNewData(askValue, footerData.euro.value);
              }
              if (item.name === 'EURUSD') {
                newFooterData.parite = createNewData(askValue, footerData.parite.value);
              }
              if (item.name === 'UHAS') {
                newFooterData.has = createNewData(askValue, footerData.has.value);
              }
            }
          });
          
          // Sadece yeni veri varsa güncelle
          if (Object.keys(newFooterData).length > 0) {
            setFooterData((prevState) => {
              const updatedState = {
                ...prevState,
                ...newFooterData,
              };
              // Footer verilerini localStorage'a kaydet
              saveFooterDataToStorage(updatedState);
              return updatedState;
            });
            setConnectionStatus('connected');
            lastMessageTimeRef.current = Date.now();
          }
        }
      }
    } catch (error) {
      console.error('Footer HTTP fallback fiyat çekme hatası:', error);
    }
  }, [createNewData, footerData.euro.value, footerData.has.value, footerData.ons.value, footerData.parite.value, footerData.usd.value, saveFooterDataToStorage]);

  const connectWebSocket = useCallback(() => {
    // Eğer zaten bağlanmaya çalışıyorsa, çık
    if (isConnectingRef.current) {
      return;
    }

    isConnectingRef.current = true;
    setConnectionStatus('connecting');

    try {
      // Önceki bağlantıyı temizle
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }

      wsRef.current = new WebSocket('wss://sslsocket.aifanet.com/5.250.255.84/24876');

      wsRef.current.onopen = () => {
        console.log('Footer WebSocket bağlantısı açıldı');
        setConnectionStatus('connected');
        retryCountRef.current = 0;
        isConnectingRef.current = false;
        lastMessageTimeRef.current = Date.now();
        
        // İlk bağlantıda fiyat talebi gönder
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({ type: 'requestUpdate' }));
        }

        // Heartbeat başlat
        heartbeatIntervalRef.current = setInterval(() => {
          if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({ type: 'heartbeat' }));
          }
        }, 15000); // Her 15 saniyede bir heartbeat
      };

      wsRef.current.onmessage = (event) => {
        try {
          lastMessageTimeRef.current = Date.now();
          const data = JSON.parse(event.data);
          const currentSymbols = symbolsRef.current;
          
          if (currentSymbols && currentSymbols.length > 0) {
            const filteredData = data.filter((item) => currentSymbols.includes(item.Code));
            const newFooterData = {};
            filteredData.forEach((item) => {
              const askValue = parseFloat(item.Ask) || 0;
              if (askValue > 0) {
                if (item.Code === 'ONS') {
                  newFooterData.ons = createNewData(askValue, footerData.ons.value);
                }
                if (item.Code === 'USDTRY') {
                  newFooterData.usd = createNewData(askValue, footerData.usd.value);
                }
                if (item.Code === 'EURTRY') {
                  newFooterData.euro = createNewData(askValue, footerData.euro.value);
                }
                if (item.Code === 'EURUSD') {
                  newFooterData.parite = createNewData(askValue, footerData.parite.value);
                }
                if (item.Code === 'UHAS') {
                  newFooterData.has = createNewData(askValue, footerData.has.value);
                }
              }
            });
            
            // Sadece yeni veri varsa güncelle
            if (Object.keys(newFooterData).length > 0) {
              setFooterData((prevState) => {
                const updatedState = {
                  ...prevState,
                  ...newFooterData,
                };
                // Footer verilerini localStorage'a kaydet
                saveFooterDataToStorage(updatedState);
                return updatedState;
              });
            }
          }
        } catch (error) {
          console.error('Footer WebSocket mesajı parse edilirken hata oluştu:', error);
        }
      };

      wsRef.current.onclose = (event) => {
        console.log('Footer WebSocket bağlantısı kapandı:', event.code, event.reason);
        setConnectionStatus('disconnected');
        isConnectingRef.current = false;
        
        // Heartbeat'i durdur
        if (heartbeatIntervalRef.current) {
          clearInterval(heartbeatIntervalRef.current);
          heartbeatIntervalRef.current = null;
        }
        
        // Sadece beklenmeyen kapanmalarda yeniden bağlan
        if (event.code !== 1000 && retryCountRef.current < 10) {
          retryCountRef.current++;
          const delay = Math.min(10000 * Math.pow(1.5, retryCountRef.current - 1), 120000); // Daha agresif yeniden bağlanma
          console.log(`Footer yeniden bağlanma denemesi ${retryCountRef.current}/10, ${delay}ms sonra`);
          
          reconnectTimeoutRef.current = setTimeout(() => {
            connectWebSocket();
          }, delay);
        } else if (retryCountRef.current >= 10) {
          console.error("Footer maksimum yeniden bağlanma denemesi aşıldı. WebSocket devre dışı.");
          setConnectionStatus('failed');
        }
      };

      wsRef.current.onerror = (error) => {
        // Sadece ilk birkaç hatada detaylı log, sonrasında sessiz
        if (retryCountRef.current < 3) {
          console.error('Footer WebSocket hatası:', error);
        }
        setConnectionStatus('error');
        isConnectingRef.current = false;
        
        // Hata durumunda bağlantıyı kapat
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.close();
        }
        
        // WebSocket başarısız olursa HTTP fallback'i aktif et
        if (retryCountRef.current >= 3 && !useHttpFallback) {
          console.log("Footer: WebSocket başarısız, HTTP fallback'e geçiliyor");
          setUseHttpFallback(true);
        }
      };
    } catch (error) {
      console.error('Footer WebSocket bağlantısı kurulurken hata:', error);
      setConnectionStatus('error');
      isConnectingRef.current = false;
      
      // Bağlantı kurulamazsa yeniden dene veya HTTP fallback'e geç
      if (retryCountRef.current < 10) {
        retryCountRef.current++;
        const delay = 30000;
        reconnectTimeoutRef.current = setTimeout(() => {
          connectWebSocket();
        }, delay);
      } else if (!useHttpFallback) {
        console.log("Footer: WebSocket başarısız, HTTP fallback'e geçiliyor");
        setUseHttpFallback(true);
      }
    }
  }, [createNewData, footerData.euro.value, footerData.has.value, footerData.ons.value, footerData.parite.value, footerData.usd.value, useHttpFallback, saveFooterDataToStorage]);

  // Bağlantı sağlığını kontrol et
  useEffect(() => {
    const healthCheckInterval = setInterval(() => {
      const now = Date.now();
      const timeSinceLastMessage = now - lastMessageTimeRef.current;
      
      // Son 1 dakikada mesaj alınmadıysa bağlantıyı yeniden kur
      if (timeSinceLastMessage > 60000 && wsRef.current) {
        console.log('Footer: Son mesajdan bu yana çok zaman geçti, bağlantıyı yeniliyorum');
        wsRef.current.close();
      }
    }, 30000); // Her 30 saniyede kontrol et

    return () => {
      clearInterval(healthCheckInterval);
    };
  }, []);

  // Component mount olduğunda WebSocket bağlantısını kur
  useEffect(() => {
    // Sayfa yüklendiğinde hemen veri çek
    // İlk olarak HTTP ile hızlı veri çek
    fetchFooterDataViaHttp();
    // Sonra WebSocket bağlantısını kur
    connectWebSocket();

    // Periyodik fiyat talebi için interval
    const intervalId = setInterval(() => {
      if (useHttpFallback) {
        // HTTP fallback kullanılıyorsa HTTP ile fiyat çek
        fetchFooterDataViaHttp();
      } else if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        // WebSocket kullanılıyorsa WebSocket ile fiyat talebi gönder
        wsRef.current.send(JSON.stringify({ type: 'requestUpdate' }));
      }
    }, 20000); // 20 saniyede bir fiyat talebi

    return () => {
      // Cleanup
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
      clearInterval(intervalId);
      isConnectingRef.current = false;
      retryCountRef.current = 0;
    };
  }, [connectWebSocket, useHttpFallback, fetchFooterDataViaHttp]); // Gerekli dependency'ler eklendi

  // HTTP fallback aktif olduğunda ilk fiyat çekmeyi yap
  useEffect(() => {
    if (useHttpFallback) {
      console.log("Footer: HTTP fallback aktif, fiyat verileri çekiliyor");
      fetchFooterDataViaHttp();
    }
  }, [useHttpFallback, fetchFooterDataViaHttp]);

  const formatValue = (value) => {
    return `${value.toFixed(2)}`;
  };

  return (
    <Box
      sx={{
        width: '100%',
        backgroundColor: '#001a5e',
        color: 'white',
        textAlign: 'center',
        fontFamily: 'Arial, sans-serif',
        fontWeight: 'bold',
        p: 0,
        display: 'flex',
        justifyContent: 'center',
        flexWrap: 'wrap',
        zIndex: 9999,
        minHeight: '60px',
        alignItems: 'center',
        boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.3)',
        marginTop: '0px',
        position: 'relative',
      }}
    >
      {/* Bağlantı durumu göstergesi */}
      <Box
        sx={{
          position: 'absolute',
          top: '5px',
          right: '10px',
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          backgroundColor: connectionStatus === 'connected' ? '#28a745' : 
                          connectionStatus === 'connecting' ? '#ffc107' : 
                          connectionStatus === 'error' ? '#dc3545' : '#6c757d',
          animation: connectionStatus === 'connected' ? `${blink} 1s infinite` : 
                    connectionStatus === 'connecting' ? `${pulse} 1s infinite` : 'none',
        }}
      />
      {Object.entries(footerData).map(([key, { value, direction }]) => (
        <Box key={key} sx={{ m: 1, display: 'flex', alignItems: 'center', fontSize: { xs: '35px', md: '20px', lg: '33px' } }}>
          <Typography 
            component="span" 
            sx={{ mr: 1, fontSize: '1.5em', fontWeight: 'bold' }}
          >
            {key === 'parite' ? '€/$' : key.toUpperCase()}:
          </Typography>
          <Typography 
            component="span" 
            sx={{ display: 'flex', alignItems: 'center', fontWeight: 'bold', fontSize: '1.5em' }}
          >
            {formatValue(value)}
            {direction === 'up' ? (
              <ArrowDropUpIcon sx={{ color: '#00ff00', fontSize: '1.4em' }} />
            ) : (
              <ArrowDropDownIcon sx={{ color: '#ff0000', fontSize: '1.2em' }} />
            )}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

export default Footer;